// In-memory cart model for the v2 POS architecture.
//
// The cart is FE state until the cashier hits Hold or Pay. This file owns
// the canonical TS shape -- the matching BE serializer is
// `project/frontend/models/v2/serialize.py::_serialize_item`. The two must
// stay in sync; any field added on one side must land on the other in the
// same PR.
//
// Lifecycle:
//   add/update/remove → local mutations (this module)
//   any basket-affecting change → debounced /cart/preview (api/cart.ts)
//   Hold or Pay → /cart/commit (api/cart.ts) → server-side receipt
//
// Per-line UI state machine (see addendum in docs/POS_V2_PLAN_INMEMORY.md):
//
//                 preview returns matching price
//   pending  ───────────────────────────────────►  confirmed
//      │                                              ▲
//      │      preview returns DIFFERENT price        │
//      └────────────────────────────────────────►  drifted   (UI pulses)
//                 │                                    │
//                 │ next mutation re-enters pending ───┘
//                 │
//                 │  preview returns "available: false"
//                 └─────────────────────────────►  unavailable
//
// "drifted" exists so the FE can animate the price cell on the line that
// the BE just adjusted; "unavailable" disables the [Pay] button until the
// cashier removes/reduces/substitutes the line.

export type CartLineState = 'pending' | 'confirmed' | 'drifted' | 'unavailable'

/** Server-priced view of one cart line, populated from /cart/preview. */
export interface PricedLine {
  /** Server-canonical unit price after schedule + tier resolution. */
  price: string
  discount: string | number | null
  discount_amount: string
  subtotal: string
  total: string
  taxes: string
  deposit_amount: string | number | null
  recycling_amount: string | number | null
  is_cannabis: boolean
  /** The DiscountSchedule that won find_one for this line, or null. */
  applied_schedule: {
    id: number
    title: string
    discount_type: number
    discount_amount: string | number
  } | null
  /** Next qty breakpoint above the current qty on the applied schedule. */
  next_tier: {
    qty_threshold: string | number
    discount_amount: string | number
  } | null
  /** True iff inventory check at preview time said the line is available. */
  available: boolean
  /** Server-resolved batch/lot for this line. The BE returns this on
   *  every priced row even when the FE submitted the line without a
   *  pinned lot — `applyPreviewToLines` uses it to back-fill
   *  `CartLine.lot_number` so the modal-from-cart and CartItemRow
   *  always show the actual batch the receipt will commit. The wire
   *  field is `batch_number`; some legacy responses use `lot_number`,
   *  which we accept as a fallback. */
  batch_number?: string | null
  lot_number?: string | null
}

/** One in-memory cart line. Stable across re-renders via `localId`. */
export interface CartLine {
  /** Stable client-generated id. Used for React keys + animation tracking. */
  localId: string
  product_id: number
  qty: number
  /** lot_number is part of the line's identity for grouping/dedup. */
  lot_number: string | null
  /** Optional cashier-supplied unit price override. */
  manual_price?: string | number | null
  /** Last priced view from /cart/preview; null until first preview returns. */
  priced: PricedLine | null
  state: CartLineState
  /**
   * Snapshot used for instant render before the first preview returns.
   * Derived from the Product the cashier scanned/tapped; never authoritative
   * for math.
   */
  display: {
    description: string
    image: string | null
    /** Pre-preview estimated unit price (Product.price). */
    estimated_price: number
    /** Customer-facing chips: brand and unit-of-measure. The cashier-side
     *  cart row shows brand inline with name; the CFD renders the smaller
     *  "Brand · 7g" subtitle line where the lot used to live. Optional
     *  because some legacy/inventory items don't carry a brand. */
    brand_name?: string | null
    unit_name?: string | null
    /** Catalog "compare at" price for the sale strikethrough on the CFD
     *  receipt row. Only meaningful when `on_sale` is true. */
    compare_price?: number | null
    on_sale?: boolean
  }
  /** Gift-card line marker. Set when this line was added via
   *  /receipts/{id}/items with `gift_card_number` + `gift_card_balance`.
   *  `cartToRequest` forwards both onto the wire so /cart/preview prices
   *  the gift-card SKU at the cashier-supplied balance instead of the
   *  catalog $0. Also part of `cartFingerprint`'s lineKey so the preview
   *  hook re-fires when a gift card is added/removed. */
  gift_card_number?: string | null
  gift_card_balance?: number | null
  /** Per-line dollar discount applied by the cashier (legacy
   *  `discount_item` scope). Stored as the LINE-TOTAL discount value
   *  (per-unit × qty); the cashier-facing label and the per-line subtotal
   *  math both expect line-total here.
   *
   *  IMPORTANT: the v2 wire contract expects PER-UNIT discount on
   *  `items[].discount_amount`. `cartToRequest` (api/cart.ts) converts
   *  by dividing this stored value by qty before sending. Don't swap
   *  the storage convention without also flipping that conversion —
   *  see the "Wire convention" block in cartToRequest.
   *
   *  When the cashier enters a percent in the discount modal scoped to a
   *  line, we compute the dollar amount before storing here so the
   *  storage shape stays consistent (line-total).
   *
   *  Mutually exclusive with `Cart.discount`: the BE silently drops one
   *  side when both are present. The UI should reject one when the other
   *  is active. */
  discount_amount?: number | null
  /** Optional reason metadata shipped to the receipt audit trail. The
   *  backend doesn't echo these in the cart preview today, but persists
   *  them onto the receipt at commit time. */
  discount_reason_id?: number | null
  discount_reason_other?: string | null
  /** Scan-to-confirm-lot state, mirrored from posStore.cart by the preview
   *  bridge. `cartToRequest` forwards it on /cart/commit so an already-
   *  scanned cannabis line persists confirmed and a recall/delivery-start
   *  doesn't re-prompt. Undefined on lines that aren't scan-gated. */
  scan_confirmed?: boolean
}

/** Customer display info sent to the CFD over BroadcastChannel.
 *  We deliberately ship a small projection rather than the full Customer
 *  object -- the CFD only needs the name to render "Welcome back, X" and
 *  a couple of compliance fields. Full Customer stays in posStore. */
export interface CartCustomerDisplay {
  id: number
  display_name: string | null
  first_name: string | null
  last_name: string | null
  /** Daily cannabis purchase limit in grams (defaults to 30 in v1). */
  flowers_limit_grams: number | null
  /** Already-purchased grams today across all stores. */
  daily_purchased_grams: number | null
}

/** Receipt-level totals returned by /cart/preview's `totals` field. */
export interface PreviewTotals {
  subtotal: string
  taxes: string
  total: string
  discount_amount: string
  /** What a SpringBig percent redemption riding `Cart.discount` actually
   *  took off the cart. `discount_amount` is every line discount, so on a
   *  cart holding sale/aged items it also counts the schedule savings the
   *  redemption stacks on. Only sent for SpringBig redemptions, and absent
   *  on older backends -- use `cartDiscountDisplayAmount`. */
  loyalty_discount_amount?: string | null
  /** The base a receipt-level $ discount is judged against (the eligible
   *  line amounts, before the receipt discount). What a receipt-scope manager
   *  approval is bound to (lib/discountLimit `eligibleBaseOf`). Absent on
   *  older backends. */
  discount_eligible_subtotal?: string | null
  deposit_amount: string | null
  recycling_amount: string | null
  amount_to_pay: string
}

/** True when a SpringBig percent offer/reward rides the cart discount. */
export function isRedemptionDiscount(discount: CartDiscount | null | undefined): boolean {
  return !!(discount && (discount.offer_id || discount.reward_id))
}

/** Dollar figure for the cart-discount row / loyalty banner.
 *
 *  For a SpringBig redemption it is the BE's `loyalty_discount_amount`.
 *  `null` there means the preview was priced without a redemption (the
 *  last settled preview before the cashier applied one), so the value is
 *  unknown and 0 is shown until the next preview lands -- falling back to
 *  `discount_amount` would show the sale/aged savings as the offer's value.
 *  Only an ABSENT field (older backend, pre-deploy persisted preview) falls
 *  back to `discount_amount`. Manual discounts always use `discount_amount`.
 *  Floored at 0: a redemption that replaced a larger cashier line discount
 *  nets negative, and it saved the customer nothing. */
export function cartDiscountDisplayAmount(
  totals: PreviewTotals | null | undefined,
  discount: CartDiscount | null | undefined,
): number {
  if (!totals) return 0
  let raw: string | null | undefined = totals.discount_amount
  if (isRedemptionDiscount(discount) && totals.loyalty_discount_amount !== undefined) {
    raw = totals.loyalty_discount_amount
  }
  return Math.max(parseFloat(raw ?? '0') || 0, 0)
}

/** True when a SpringBig redemption is applied and the settled preview says
 *  it took nothing off (the BE floors its value at 0 -- e.g. a MAX-mode
 *  store, or it replaced a larger discount). The offer is still spent at
 *  commit, so the cashier should know. False while the value is unknown. */
export function redemptionSavesNothing(
  totals: PreviewTotals | null | undefined,
  discount: CartDiscount | null | undefined,
): boolean {
  const raw = totals?.loyalty_discount_amount
  if (!isRedemptionDiscount(discount) || raw == null) return false
  // Below half a cent it displays as $0.00. A non-numeric string parses to
  // NaN, which compares false: garbage is unknown, not "saved nothing".
  return parseFloat(raw) < 0.005
}

/** Customer-display breakdown of `discount_amount`: the redemption's own
 *  value (`loyalty`, null when no redemption rides the cart) and everything
 *  else (`savings` -- sale/aged schedule discounts). They sum to
 *  `discount_amount`, so the customer sees the same total as before. */
export function splitCartDiscount(
  totals: PreviewTotals | null | undefined,
): { savings: number; loyalty: number | null } {
  if (!totals) return { savings: 0, loyalty: null }
  const total = parseFloat(totals.discount_amount ?? '0') || 0
  const raw = totals.loyalty_discount_amount
  if (raw == null) return { savings: total, loyalty: null }
  const loyalty = Math.min(Math.max(parseFloat(raw) || 0, 0), Math.max(total, 0))
  return { savings: Math.round((total - loyalty) * 100) / 100, loyalty }
}

/** /cart/preview warning shapes -- shown to the cashier in the cart UI. */
export interface PreviewWarning {
  kind: 'out_of_stock' | 'cannabis_limit' | 'ops_error' | string
  product_id?: number
  detail?: string
  available?: number
  requested?: number
}

/** Response payload from /cart/preview, modulo non-cart receipt fields. */
export interface PreviewResponse {
  id: number
  number: string
  totals: PreviewTotals
  amount_to_pay: string
  items: Array<PricedLine & {
    id: number
    product_id: number
    qty: number
    description: string | null
    main_image: string | null
  }>
  warnings: PreviewWarning[]
  /** BE-resolved customer for this cart. Set even when the cashier
   *  hasn't picked one — the BE assigns the configured retail/guest
   *  customer (e.g. id 3332). Used by `usePricingKey` as the fallback
   *  customer_id for `/objects/products/batches` so guest carts still
   *  get the right group/price_field resolution server-side, instead
   *  of falling through to the catalog default. */
  customer_id?: number | null
  /** Manager approvals for over-limit discounts on this receipt (see
   *  lib/discountLimit.ts). Always present on a current backend; absent on
   *  an older one. Cashier-side only: the CFD snapshot never carries it. */
  discount_overrides?: DiscountOverrideRow[]
}

/** One manager approval of an over-limit discount, as the receipt payload
 *  (`serialize.py`) exposes it. `item_id` is null for a receipt-level
 *  discount. `cancelled_at` is set once the discount was removed/voided; the
 *  row stays for the audit trail but is no longer shown as "approved by". */
export interface DiscountOverrideRow {
  scope: 'receipt' | 'line' | 'price'
  item_id: number | null
  override_by_name: string | null
  percent: string | number | null
  amount: string | number | null
  cancelled_at?: string | null
  /** The bound tuple (approval binding contract). Optional: absent on
   *  older backends, in which case only scope + value are compared. */
  product_id?: number | null
  lot?: string | null
  qty?: string | number | null
  base_amount?: string | number | null
}

export type CartPreviewStatus = 'idle' | 'fetching' | 'stale' | 'error'

/** Display projection of a single partial payment. Used by the CFD to
 *  render the "Cash -$10" rows the legacy template showed. The cashier-
 *  facing CheckoutModal also reads from the receipt's payments array, but
 *  this is the wire shape — only the label + amount, no ids. */
export interface CartPartialPayment {
  payment_method: string | null
  amount: string
  /** Integrated-terminal tip portion of `amount` (tip-inclusive); '0'
   *  when untipped. Mirrors cartChannel.CartPaymentDisplay.tip_amount —
   *  keep the two shapes aligned (the snapshot builder passes this
   *  array through verbatim). */
  tip_amount?: string
}

/** Cashier-applied receipt-level discount. Stored on the cart so it can be
 *  re-included on every /cart/preview and /cart/commit call, keeping the
 *  cart and the eventual receipt in lockstep. Exactly one of percent /
 *  amount / coupon should be set; the rest are tracking metadata for the
 *  reason audit trail (legacy reason_id / reason_other shape). */
export interface CartDiscount {
  percent?: number
  amount?: number
  coupon?: string
  reason_id?: number
  reason_other?: string
  /** SpringBig attribution. Set (alongside `percent`) when this cart
   *  discount IS a percent loyalty offer/reward applied PRE-TAX, rather than
   *  a cashier manual discount. The BE (cart.py) reads these off the
   *  `discount` body to stamp the receipt's springbig_offer_id/reward_id and
   *  report the redemption after payment -- they don't affect pricing, so
   *  they're deliberately left out of the preview fingerprint. Exactly one of
   *  offer_id / reward_id is set, and only for the percent case (flat
   *  redemptions stay post-tax type-299 payments). One discount source per
   *  sale: mutually exclusive with a cashier manual discount. */
  offer_id?: number
  reward_id?: number
  /** Approving manager's PIN for an over-limit discount. WIRE ONLY: added
   *  by `cartToRequest` from cartStore.discountApprovals (lib/discountLimit),
   *  never stored on `Cart.discount` (which is persisted). */
  override_pin?: string
}

/** Loyalty state mirrored to the customer-facing display. POS owns the
 *  source data (SpringBig availability from /customers/:id/springbig and the
 *  staged redemption in posStore); CustomerHeader pushes this projection into
 *  the cart so the existing broadcast pipeline carries it to the CFD. Null
 *  when no customer is attached. */
export interface CartLoyalty {
  /** Selected customer has redeemable rewards or offers waiting (enrolled +
   *  at least one reward or offer). Drives the CFD "Rewards Available" badge. */
  available: boolean
  /** Dollar value of the staged reward/offer redemption, 0 when nothing is
   *  staged. Drives the CFD "Loyalty −$X" credit line and the reduced
   *  "To collect" amount. Percent redemptions are resolved to dollars POS-side
   *  against the live cart total, matching the cashier's chip. */
  stagedAmount: number
}

/** The whole in-memory cart. One per register. */
export interface Cart {
  customer_id: number | null
  /** Display projection of the selected customer. Source of truth for the
   *  full Customer object stays in posStore; this is what we ship to the
   *  CFD over BroadcastChannel. Null when no customer is selected. */
  customer: CartCustomerDisplay | null
  exempt_taxes: boolean
  lines: CartLine[]
  /** Last full preview response (totals, warnings, mix-match opportunities). */
  preview: PreviewResponse | null
  previewStatus: CartPreviewStatus
  /** When the last successful preview returned. Used to gate stale-on-pay. */
  previewedAt: number | null
  /** Partial payments already tendered against the cart's backing receipt.
   *  Populated by CheckoutModal after each /payments call; cleared by
   *  `clear()`. Null when no receipt exists or no partial has landed. */
  partialPayments: CartPartialPayment[] | null
  /** Remaining balance after partial payments (mirror of receipt
   *  `amount_to_pay`). Null when no partial-payment context exists; in
   *  that case the CFD falls back to `preview.totals.total`. */
  partialAmountToPay: string | null
  /** Cashier-applied receipt-level discount. When set, sent on every
   *  /cart/preview and /cart/commit so the BE can factor it into totals
   *  and persist it onto the eventual receipt. Null when no discount is
   *  active. Cleared on `clear()` and after a successful commit. */
  discount: CartDiscount | null
  /** Loyalty projection for the CFD (rewards-available badge + staged
   *  redemption credit). Pushed by CustomerHeader; null when no customer is
   *  attached. Cleared with the cart on `clear()`. */
  loyalty: CartLoyalty | null
}

// ---------------------------------------------------------------------------
// Pure helpers. No I/O, no zustand, no React.
// ---------------------------------------------------------------------------

let _localIdCounter = 0
/** Stable per-process id. Suffixing the counter prevents collisions when two
 *  scans hit the same millisecond -- Date.now() alone isn't enough. */
export function makeLocalId(): string {
  _localIdCounter = (_localIdCounter + 1) | 0
  return `cl_${Date.now().toString(36)}_${_localIdCounter.toString(36)}`
}

/** Fingerprint of basket-affecting state. Two carts with the same fingerprint
 *  produce the same /cart/preview output, so the preview hook can no-op when
 *  rapid mutations cycle back to the same shape (`+qty +qty -qty`). */
export function cartFingerprint(cart: Cart): string {
  // Every field cartToRequest puts on the wire, including the metadata-only
  // ones (discount reason, scan_confirmed): a change there must re-preview,
  // or the cart sits on 'pending' for a shape the hook thinks it priced.
  const lineKey = (l: CartLine) =>
    `${l.product_id}|${l.qty}|${l.lot_number ?? ''}|${l.manual_price ?? ''}|d${l.discount_amount ?? ''}|g${l.gift_card_number ?? ''}`
    + `|ri${l.discount_reason_id ?? ''}|ro${l.discount_reason_other ?? ''}|s${l.scan_confirmed === undefined ? '' : l.scan_confirmed ? 1 : 0}`
  // Sort so insertion order doesn't change the fingerprint.
  const lines = [...cart.lines].map(lineKey).sort().join(';')
  // Cart-level discount is part of the pricing input; changing it must
  // refire preview.
  const d = cart.discount
  const discountKey = d
    ? `p${d.percent ?? ''}|a${d.amount ?? ''}|c${d.coupon ?? ''}|r${d.reason_id ?? ''}|o${d.reason_other ?? ''}`
    : ''
  return `${cart.customer_id ?? ''}|${cart.exempt_taxes ? '1' : '0'}|${lines}|${discountKey}`
}

/** Build a `CartLine` from a Product the cashier just scanned/tapped. The
 *  result enters `pending` state so the UI shows the "•" indicator until
 *  /cart/preview confirms or adjusts. */
export function newCartLine(args: {
  product_id: number
  qty: number
  lot_number?: string | null
  manual_price?: string | number | null
  gift_card_number?: string | null
  gift_card_balance?: number | null
  scan_confirmed?: boolean
  display: CartLine['display']
}): CartLine {
  return {
    localId: makeLocalId(),
    product_id: args.product_id,
    qty: args.qty,
    lot_number: args.lot_number ?? null,
    manual_price: args.manual_price ?? null,
    gift_card_number: args.gift_card_number ?? null,
    gift_card_balance: args.gift_card_balance ?? null,
    ...(args.scan_confirmed !== undefined
      ? { scan_confirmed: args.scan_confirmed }
      : {}),
    priced: null,
    state: 'pending',
    display: args.display,
  }
}

/** Empty cart for a fresh sale. */
export function emptyCart(): Cart {
  return {
    customer_id: null,
    customer: null,
    exempt_taxes: false,
    lines: [],
    preview: null,
    previewStatus: 'idle',
    previewedAt: null,
    partialPayments: null,
    partialAmountToPay: null,
    discount: null,
    loyalty: null,
  }
}

/** Apply a /cart/preview response to existing cart lines. Pure; returns a new
 *  lines array. Lines unchanged if no matching priced row came back (e.g. the
 *  preview crossed in flight with another mutation -- the next preview will
 *  reconcile). */
export function applyPreviewToLines(
  lines: CartLine[],
  preview: PreviewResponse,
): CartLine[] {
  // Match server priced rows back to local lines by (product_id, lot).
  // The BE returns the resolved lot as `batch_number` (legacy responses
  // used `lot_number`); we accept either as the lot key. localId is not
  // stable across the BE round-trip, so it isn't usable for this map.
  const lotOf = (priced: PricedLine): string | null =>
    priced.batch_number ?? priced.lot_number ?? null
  const byKey = new Map<string, PricedLine>()
  const byProduct = new Map<number, PricedLine[]>()
  for (const it of preview.items) {
    const priced = it as unknown as PricedLine
    byKey.set(`${it.product_id}|${lotOf(priced) ?? ''}`, priced)
    const list = byProduct.get(it.product_id) ?? []
    list.push(priced)
    byProduct.set(it.product_id, list)
  }
  return lines.map((line) => {
    const key = `${line.product_id}|${line.lot_number ?? ''}`
    let priced: PricedLine | null = byKey.get(key) ?? null
    if (priced === null) {
      // Fallback when FE and BE disagree on the lot — either the line
      // had no lot pinned yet (optimistic add before the batches fetch
      // resolved) or the BE substituted a different batch. Match by
      // product_id alone when there's exactly one priced row for this
      // product so we don't accidentally collapse two distinct lots
      // onto the same row. Mirrors the same fallback in
      // posStore.applyServerItemPrices.
      const candidates = byProduct.get(line.product_id) ?? []
      if (candidates.length === 1) priced = candidates[0]
    }
    if (priced === null) {
      // No priced row came back for this line. Leave it pending so the next
      // preview cycle has a chance to reconcile -- don't flip to confirmed
      // on missing data.
      return line
    }
    let state: CartLineState
    if (priced.available === false) {
      state = 'unavailable'
    } else if (
      line.priced !== null
      && priceCellChanged(line.priced, priced)
    ) {
      // We had a prior priced view AND the new one differs => animate.
      state = 'drifted'
    } else if (estimateChanged(line, priced)) {
      // First preview, and it disagrees with the FE estimate => animate.
      state = 'drifted'
    } else {
      state = 'confirmed'
    }
    // Do NOT back-fill `lot_number` from the priced row. The cart's
    // lot is the cashier's pinned selection (or what handleAddToCart
    // optimistically chose); the BE may pick a *different* batch to
    // price against during preview consolidation, and overwriting the
    // local lot with that pick causes two distinct lot lines to
    // collapse onto the same lot — both get re-sent as the same
    // lot_number on the next preview, the BE consolidates them again,
    // and the cart loop is stuck. The modal's seed handles the
    // unpinned case via `previewBatchNumber` directly without needing
    // the cart line to mutate.
    return { ...line, priced, state }
  })
}

function priceCellChanged(prev: PricedLine, next: PricedLine): boolean {
  return prev.total !== next.total || prev.price !== next.price
}

function estimateChanged(line: CartLine, priced: PricedLine): boolean {
  // unit_price * qty within $0.01 of priced.total => no animation needed.
  const estimated = (line.display.estimated_price || 0) * line.qty
  const actual = parseFloat(priced.total)
  if (!Number.isFinite(actual)) return true
  return Math.abs(estimated - actual) > 0.01
}
